import type { ReactNode } from 'react'
import {
  TIMEFRAME_FILTER,
  type CardStub,
  type DashboardFilters,
  type DashboardSection,
} from '@/features/dashboard/api/schemas'
import { TimeframeFilters } from '@/features/dashboard/filters/SectionFilters'
import { HeaderIconButton } from '@/shared/ui/HeaderIconButton'
import { TableIcon } from '@/shared/ui/icons'
import { MarketChips } from '@/features/dashboard/filters/MarketChips'
import { useSectionSelection } from '@/features/dashboard/filters/useDashboardFilters'
import { cx } from '@/shared/lib/classNames'
import { useStuck } from '@/shared/lib/useStuck'
import { useViewAsTable } from '@/features/dashboard/filters/useViewAsTable'
import styles from './DeepDiveHeader.module.css'

type Props = {
  children: ReactNode
  /** The headline card and its section, whose timeframe selection is the page's. */
  home?: { stub: CardStub; section: DashboardSection } | undefined
  viewToggle?: boolean
  /** Customers deep dives only: the market chips, in a second row. */
  markets?: DashboardFilters['markets'] | undefined
}

/** Sticks below the app header when it has a control or chips; otherwise static. */
export const DeepDiveHeader = ({
  children,
  home,
  viewToggle = false,
  markets,
}: Props) => {
  const { selection } = useSectionSelection(home?.section.id)
  const { asTable, setAsTable } = useViewAsTable()
  const showTimeframe =
    (home?.stub.filters?.includes(TIMEFRAME_FILTER) ?? false) && !!selection
  const shown = showTimeframe || viewToggle
  const sticky = shown || !!markets
  const { sentinel, header, stuck } = useStuck(sticky)

  return (
    <>
      {sticky && (
        <div ref={sentinel} className={styles.sentinel} aria-hidden='true' />
      )}
      <div
        ref={header}
        className={cx(
          styles.header,
          sticky && styles.sticky,
          stuck && styles.stuck
        )}
      >
        <div className={styles.row}>
          <div className={styles.heading}>{children}</div>
          {shown && (
            <div className={styles.controls}>
              {showTimeframe && home && (
                <div
                  className={styles.control}
                  role='group'
                  aria-label='Timeframe'
                >
                  <TimeframeFilters section={home.section} />
                </div>
              )}
              {showTimeframe && viewToggle && (
                <span className={styles.separator} aria-hidden='true' />
              )}
              {viewToggle && (
                <HeaderIconButton
                  className={styles.toggle ?? ''}
                  icon={<TableIcon />}
                  aria-label='View as table'
                  title='View as table'
                  aria-pressed={asTable}
                  onClick={() => setAsTable(!asTable)}
                />
              )}
            </div>
          )}
        </div>
        {markets && <MarketChips markets={markets} />}
      </div>
    </>
  )
}
